import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Spin, Button, Empty } from 'antd';
import { useProjectTeamActivity } from '@hooks/projects/detail/useProjectDetail';
import UserAvatar from '@/components/ui/UserAvatar';
import SidePanel from '@/components/ui/SidePanel/SidePanel';
import { getAvatarColor, getInitials } from '@hooks/projects/projectHelpers';
import { resolveProfileImageUrl, getMemberRoleLabel } from '@hooks/projects/detail/useProjectDetail';
import { ENV } from '@configs/env';
import { DotWaveLoader } from '@/pages/style';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_TEAM_ACTIVITY_VIEW } from '@/ability';

export default function TeamActivityPanel({ projectId, open, onClose, assignedUsers = [] }) {
    const can = useCanCheck();
    const canViewAllActivity = can(PROJECT_TEAM_ACTIVITY_VIEW);
    const [page, setPage] = useState(1);
    const [allActivities, setAllActivities] = useState([]);
    const limit = 15; // Clean pagination size for drawer display
    const sentinelRef = React.useRef(null);

    const { data, isLoading, isFetching } = useProjectTeamActivity(projectId, { page, limit }, {
        enabled: open && !!projectId,
    });

    // Reset when panel opens/closes or projectId changes
    useEffect(() => {
        if (open) {
            setPage(1);
            setAllActivities([]);
        }
    }, [open, projectId]);

    useEffect(() => {
        if (data?.items) {
            if (page === 1) {
                setAllActivities(data.items);
            } else {
                // Append unique items
                setAllActivities((prev) => {
                    const existingIds = new Set(prev.map(item => item.id));
                    const newItems = data.items.filter(item => !existingIds.has(item.id));
                    return [...prev, ...newItems];
                });
            }
        }
    }, [data, page]);

    const hasMore = data ? allActivities.length < data.total : false;

    useEffect(() => {
        if (!hasMore || isLoading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isFetching) {
                    setPage((p) => p + 1);
                }
            },
            { threshold: 0.1 }
        );

        const currentSentinel = sentinelRef.current;
        if (currentSentinel) {
            observer.observe(currentSentinel);
        }

        return () => {
            if (currentSentinel) {
                observer.unobserve(currentSentinel);
            }
        };
    }, [hasMore, isLoading, isFetching, allActivities.length]);

    const mappedActivities = React.useMemo(() => {
        return allActivities.map((item, idx) => {
            const userName = item.user?.name || 'Unknown User';
            const projectUser = assignedUsers.find(u => String(u.id || u.userId || u._id) === String(item.user?.id));
            const role = projectUser ? getMemberRoleLabel(projectUser) : 'Contributor';
            
            // Format time difference cleanly (e.g. "5m ago", "2h ago", "1d ago")
            let relativeTime = 'Just now';
            if (item.createdAt) {
                try {
                    const diffMs = Date.now() - new Date(item.createdAt).getTime();
                    const diffMins = Math.floor(diffMs / 60000);
                    const diffHours = Math.floor(diffMins / 60);
                    const diffDays = Math.floor(diffHours / 24);
                    
                    if (diffDays > 0) relativeTime = `${diffDays}d ago`;
                    else if (diffHours > 0) relativeTime = `${diffHours}h ago`;
                    else if (diffMins > 0) relativeTime = `${diffMins}m ago`;
                } catch (e) {
                    relativeTime = 'Recent';
                }
            }

            let actionText = item.message || 'performed an action';
            if (item.message && userName && item.message.toLowerCase().startsWith(userName.toLowerCase())) {
                actionText = item.message.substring(userName.length).trim();
            }

            return {
                id: item.id || idx,
                user: userName,
                action: actionText,
                time: relativeTime,
                avatarBg: getAvatarColor(item.user?.id || idx),
                role,
                imageUrl: item.user?.avatarUrl ? resolveProfileImageUrl(item.user.avatarUrl, ENV.API_URL) : null,
                initials: getInitials(userName)
            };
        });
    }, [allActivities, assignedUsers]);

    return (
        <SidePanel
            open={open}
            onClose={onClose}
            title={`${canViewAllActivity ? 'Team' : 'My'} Activity${data?.total ? ` (${data.total})` : ''}`}
            width="480px"
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, height: '100%' }}>
                {isLoading && allActivities.length === 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                        <Spin size="large" />
                    </div>
                ) : mappedActivities.length === 0 ? (
                    <Empty description="No activities found" style={{ marginTop: 80 }} />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0 24px 0' }}>
                        {mappedActivities.map((item) => (
                            <div key={item.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingBottom: 16, borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)' }}>
                                <UserAvatar
                                    name={item.user}
                                    role={item.role}
                                    imageUrl={item.imageUrl}
                                    initials={item.initials || (item.user ? item.user.charAt(0) : 'U')}
                                    avatarBg={item.avatarBg}
                                    disableExpand={true}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                                    <div style={{ fontSize: 13, color: 'var(--ant-color-text, #111827)', lineHeight: '18px' }}>
                                        <span style={{ fontWeight: 600 }}>{item.user}</span> {item.action}
                                    </div>
                                    <span style={{ fontSize: 11, color: 'var(--ant-color-text-secondary, #6b7280)' }}>
                                        {item.time}
                                    </span>
                                </div>
                            </div>
                        ))}

                        {hasMore && (
                            <div ref={sentinelRef} style={{ display: 'flex', justifyContent: 'center', padding: '16px 0', width: '100%' }}>
                                <DotWaveLoader>
                                    <span style={{ background: 'var(--ant-color-primary, #7543DC)' }} />
                                    <span style={{ background: 'var(--ant-color-primary, #7543DC)' }} />
                                    <span style={{ background: 'var(--ant-color-primary, #7543DC)' }} />
                                </DotWaveLoader>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SidePanel>
    );
}

TeamActivityPanel.propTypes = {
    projectId: PropTypes.string.isRequired,
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    assignedUsers: PropTypes.array,
};
